import { useState, useEffect } from 'react';
import { useOutletContext } from 'react-router-dom';
import API from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Switch } from '../components/ui/switch';
import { toast } from 'sonner';
import { Plus, Trash, PaperPlaneTilt, CalendarBlank } from '@phosphor-icons/react';

export default function ChannelBroadcastPage() {
  const { selectedBot, bots } = useOutletContext();
  const [channels, setChannels] = useState([]);
  const [reports, setReports] = useState([]);
  const [scheduledList, setScheduledList] = useState([]);
  const [addOpen, setAddOpen] = useState(false);
  const [channelUsername, setChannelUsername] = useState('');
  const [adding, setAdding] = useState(false);
  const [selectedChannels, setSelectedChannels] = useState([]);
  const [text1, setText1] = useState('');
  const [text2, setText2] = useState('');
  const [buttons, setButtons] = useState([]);
  const [mediaType, setMediaType] = useState('');
  const [mediaFile, setMediaFile] = useState(null);
  const [pollQuestion, setPollQuestion] = useState('');
  const [pollOptions, setPollOptions] = useState(['', '']);
  const [scheduledAt, setScheduledAt] = useState('');
  const [showBtnLabel, setShowBtnLabel] = useState(true);
  const [sending, setSending] = useState(false);
  const [activeTab, setActiveTab] = useState('send');

  const bot = bots.find(b => b.id === selectedBot);

  const fetchData = () => {
    const params = {};
    if (selectedBot) params.bot_id = selectedBot;
    API.get('/channels', { params }).then(r => setChannels(r.data.channels || [])).catch(() => {});
    API.get('/channel-broadcast/reports', { params }).then(r => setReports(r.data.reports || [])).catch(() => {});
    API.get('/channel-broadcast/scheduled', { params }).then(r => setScheduledList(r.data.scheduled || [])).catch(() => {});
  };
  useEffect(fetchData, [selectedBot]);

  const addChannel = async (e) => {
    e.preventDefault();
    if (!selectedBot) return toast.error('Select a bot first');
    setAdding(true);
    try {
      await API.post('/channels', { bot_id: selectedBot, channel_username: channelUsername });
      toast.success('Channel added'); setAddOpen(false); setChannelUsername(''); fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
    finally { setAdding(false); }
  };

  const deleteChannel = async (id) => {
    try { await API.delete(`/channels/${id}`); toast.success('Removed'); fetchData(); } catch { toast.error('Failed'); }
  };

  const toggleChannel = (id) => setSelectedChannels(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);

  const resetForm = () => {
    setText1(''); setText2(''); setButtons([]); setMediaType(''); setMediaFile(null);
    setPollQuestion(''); setPollOptions(['', '']); setScheduledAt(''); setSelectedChannels([]);
  };

  const buildFormData = () => {
    const fd = new FormData();
    fd.append('bot_id', selectedBot);
    fd.append('channel_ids', JSON.stringify(selectedChannels));
    if (text1) fd.append('text_1', text1);
    if (text2) fd.append('text_2', text2);
    fd.append('inline_buttons', JSON.stringify(buttons));
    fd.append('show_button_label', showBtnLabel.toString());
    if (mediaType) fd.append('media_type', mediaType);
    if (mediaFile) fd.append('media_file', mediaFile);
    if (mediaType === 'poll') {
      fd.append('poll_question', pollQuestion);
      fd.append('poll_options', JSON.stringify(pollOptions.filter(o => o.trim())));
    }
    return fd;
  };

  const sendNow = async (e) => {
    e.preventDefault();
    if (selectedChannels.length === 0) return toast.error('Select channels');
    setSending(true);
    try {
      const { data } = await API.post('/channel-broadcast/send', buildFormData());
      toast.success(data.message); resetForm(); fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
    finally { setSending(false); }
  };

  const scheduleSubmit = async (e) => {
    e.preventDefault();
    if (selectedChannels.length === 0) return toast.error('Select channels');
    if (!scheduledAt) return toast.error('Set schedule time');
    setSending(true);
    try {
      const fd = buildFormData();
      fd.append('scheduled_at', scheduledAt);
      await API.post('/channel-broadcast/schedule', fd);
      toast.success('Channel broadcast scheduled'); resetForm(); fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
    finally { setSending(false); }
  };

  const deleteScheduled = async (id) => {
    try { await API.delete(`/channel-broadcast/scheduled/${id}`); toast.success('Deleted'); fetchData(); } catch { toast.error('Failed'); }
  };

  const updateButton = (idx, field, val) => {
    setButtons(prev => { const b = [...prev]; b[idx] = { ...b[idx], [field]: val }; return b; });
  };

  const updatePollOption = (idx, val) => {
    setPollOptions(prev => { const o = [...prev]; o[idx] = val; return o; });
  };

  if (!selectedBot) {
    return (
      <div className="space-y-4" data-testid="channel-broadcast-page">
        <h1 className="text-3xl sm:text-4xl font-bold tracking-tight" style={{ fontFamily: 'Cabinet Grotesk' }}>📢 Channel Broadcast</h1>
        <div className="border border-dashed border-border rounded-md p-12 text-center text-muted-foreground">Select a bot from the sidebar</div>
      </div>
    );
  }

  // Shared form fields rendered inline (NOT as a component to avoid re-mount on state change)
  const renderFormFields = (isScheduled) => (
    <>
      {isScheduled && (
        <div className="space-y-1.5">
          <Label>Schedule Time (IST)</Label>
          <Input data-testid="ch-schedule-time" type="datetime-local" value={scheduledAt} onChange={e => setScheduledAt(e.target.value)} required />
        </div>
      )}
      <div className="space-y-1.5">
        <Label>Media Type</Label>
        <select data-testid="ch-media-type" value={mediaType} onChange={e => setMediaType(e.target.value)} className="w-full border border-border rounded-md px-3 py-2 text-sm bg-background">
          <option value="">None</option>
          <option value="photo">📷 Photo</option>
          <option value="gif">🎞️ GIF</option>
          <option value="video">🎬 Video</option>
          <option value="poll">📊 Poll</option>
        </select>
      </div>
      {mediaType && mediaType !== 'poll' && (
        <div className="space-y-1.5">
          <Label>Upload {mediaType === 'photo' ? 'Image' : mediaType === 'gif' ? 'GIF' : 'Video'}</Label>
          <Input
            type="file"
            data-testid="ch-media-file"
            onChange={e => setMediaFile(e.target.files[0])}
            accept={mediaType === 'photo' ? 'image/*' : mediaType === 'gif' ? 'image/gif' : 'video/*'}
          />
          {mediaFile && (
            <div className="flex items-center gap-2 p-2 border border-border rounded bg-muted/30 text-xs">
              <span className="text-green-500">&#10003;</span>
              <span className="truncate">{mediaFile.name}</span>
              <span className="text-muted-foreground">({(mediaFile.size / 1024).toFixed(1)} KB)</span>
              <button type="button" onClick={() => setMediaFile(null)} className="ml-auto text-destructive hover:underline text-xs">Remove</button>
            </div>
          )}
        </div>
      )}
      {mediaType === 'poll' && (
        <div className="space-y-3 border border-border rounded-md p-3 bg-muted/20">
          <div className="space-y-1.5">
            <Label>Poll Question</Label>
            <Input data-testid="ch-poll-question" value={pollQuestion} onChange={e => setPollQuestion(e.target.value)} placeholder="Your question..." />
          </div>
          {pollOptions.map((opt, i) => (
            <div key={i} className="flex gap-2">
              <Input
                data-testid={`ch-poll-opt-${i}`}
                value={opt}
                onChange={e => updatePollOption(i, e.target.value)}
                placeholder={`Option ${i + 1}`}
              />
              {i >= 2 && (
                <Button type="button" variant="ghost" size="sm" onClick={() => setPollOptions(prev => prev.filter((_, j) => j !== i))}>
                  <Trash size={14} />
                </Button>
              )}
            </div>
          ))}
          <Button type="button" variant="outline" size="sm" onClick={() => setPollOptions(prev => [...prev, ''])} className="gap-1">
            <Plus size={14} /> Add Option
          </Button>
        </div>
      )}
      <div className="space-y-1.5">
        <Label>Text 1 (separate message)</Label>
        <Textarea data-testid="ch-text-1" value={text1} onChange={e => setText1(e.target.value)} rows={3} placeholder="First message..." />
      </div>
      <div className="space-y-1.5">
        <Label>Text 2 (with inline buttons)</Label>
        <Textarea data-testid="ch-text-2" value={text2} onChange={e => setText2(e.target.value)} rows={3} placeholder="Second message..." />
      </div>
      <div>
        <div className="flex items-center justify-between mb-2">
          <Label>Inline Buttons</Label>
          <Button type="button" variant="outline" size="sm" onClick={() => setButtons(prev => [...prev, { title: '', url: '' }])} className="gap-1">
            <Plus size={14} /> Add
          </Button>
        </div>
        {buttons.map((btn, i) => (
          <div key={i} className="flex gap-2 mb-2">
            <Input
              data-testid={`ch-btn-title-${i}`}
              value={btn.title}
              onChange={e => updateButton(i, 'title', e.target.value)}
              placeholder="Button title"
            />
            <Input
              data-testid={`ch-btn-url-${i}`}
              value={btn.url}
              onChange={e => updateButton(i, 'url', e.target.value)}
              placeholder="https://..."
            />
            <Button type="button" variant="ghost" size="sm" onClick={() => setButtons(prev => prev.filter((_, j) => j !== i))} className="text-destructive">
              <Trash size={14} />
            </Button>
          </div>
        ))}
        <div className="flex items-center justify-between pt-2 mt-2 border-t border-border">
          <div><Label>Show &ldquo;Please click below the Buttons :&rdquo; label</Label><p className="text-xs text-muted-foreground">When OFF, only buttons are sent (no label text)</p></div>
          <Switch data-testid="ch-show-btn-label" checked={showBtnLabel} onCheckedChange={setShowBtnLabel} />
        </div>
      </div>
    </>
  );

  return (
    <div className="space-y-6" data-testid="channel-broadcast-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl sm:text-4xl font-bold tracking-tight" style={{ fontFamily: 'Cabinet Grotesk' }}>📢 Channel Broadcast</h1>
          <p className="text-sm text-muted-foreground mt-1">Broadcast to Telegram channels via {bot?.bot_name}</p>
        </div>
        <Button data-testid="add-channel-btn" onClick={() => setAddOpen(true)} className="gap-2"><Plus size={16} /> Add Channel</Button>
      </div>

      {/* Channels selector */}
      {channels.length > 0 ? (
        <div className="border border-border rounded-md p-4">
          <h4 className="font-medium text-sm mb-3">Select Channels</h4>
          <div className="flex flex-wrap gap-2">
            {channels.map(ch => (
              <button key={ch.id} onClick={() => toggleChannel(ch.id)} className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-md border text-sm transition-all ${selectedChannels.includes(ch.id) ? 'bg-primary text-primary-foreground border-primary' : 'border-border hover:bg-accent'}`} data-testid={`channel-${ch.id}`}>
                @{ch.channel_username} <span className="text-xs opacity-70">({ch.channel_title})</span>
                <button onClick={(e) => { e.stopPropagation(); deleteChannel(ch.id); }} className="ml-1 hover:text-destructive"><Trash size={12} /></button>
              </button>
            ))}
          </div>
        </div>
      ) : (
        <div className="border border-dashed border-border rounded-md p-8 text-center text-muted-foreground">
          <p>No channels added yet. Bot must be an admin of the channel.</p>
        </div>
      )}

      {/* Tab buttons (manual tabs to avoid re-mount issues) */}
      {channels.length > 0 && (
        <>
          <div className="flex border border-border rounded-lg overflow-hidden max-w-2xl">
            <button
              data-testid="ch-tab-send"
              onClick={() => setActiveTab('send')}
              className={`flex-1 py-2.5 text-sm font-medium transition-colors ${activeTab === 'send' ? 'bg-primary text-primary-foreground' : 'hover:bg-accent'}`}
            >
              <PaperPlaneTilt size={16} className="inline mr-1.5" /> Send Now
            </button>
            <button
              data-testid="ch-tab-schedule"
              onClick={() => setActiveTab('schedule')}
              className={`flex-1 py-2.5 text-sm font-medium transition-colors ${activeTab === 'schedule' ? 'bg-primary text-primary-foreground' : 'hover:bg-accent'}`}
            >
              <CalendarBlank size={16} className="inline mr-1.5" /> Schedule
            </button>
          </div>

          {activeTab === 'send' && (
            <form onSubmit={sendNow} className="space-y-4 border border-border rounded-md p-5 max-w-2xl">
              {renderFormFields(false)}
              <Button data-testid="send-channel-broadcast" type="submit" disabled={sending || selectedChannels.length === 0} className="w-full gap-2">
                <PaperPlaneTilt size={18} /> {sending ? 'Sending...' : `Send to ${selectedChannels.length} Channel(s)`}
              </Button>
            </form>
          )}

          {activeTab === 'schedule' && (
            <form onSubmit={scheduleSubmit} className="space-y-4 border border-border rounded-md p-5 max-w-2xl">
              {renderFormFields(true)}
              <Button data-testid="schedule-channel-broadcast" type="submit" disabled={sending || selectedChannels.length === 0} className="w-full gap-2">
                <CalendarBlank size={18} /> {sending ? 'Scheduling...' : `Schedule for ${selectedChannels.length} Channel(s)`}
              </Button>
            </form>
          )}
        </>
      )}

      {/* Scheduled channel broadcasts */}
      {scheduledList.length > 0 && (
        <div className="space-y-3">
          <h3 className="text-lg font-medium" style={{ fontFamily: 'Cabinet Grotesk' }}>Scheduled Channel Broadcasts</h3>
          <div className="border border-border rounded-md overflow-x-auto">
            <table className="w-full text-sm min-w-[500px]">
              <thead><tr className="bg-muted/50 border-b border-border"><th className="text-left px-4 py-3 text-xs uppercase">Scheduled At</th><th className="text-left px-4 py-3 text-xs uppercase">Content</th><th className="text-left px-4 py-3 text-xs uppercase">Status</th><th className="text-right px-4 py-3 text-xs uppercase">Action</th></tr></thead>
              <tbody>
                {scheduledList.map((s, i) => (
                  <tr key={s.id} className={`border-b border-border ${i % 2 ? 'bg-muted/20' : ''}`}>
                    <td className="px-4 py-3 font-mono text-xs">{s.scheduled_at?.replace('T', ' ').slice(0, 16)}</td>
                    <td className="px-4 py-3 text-xs">{s.media_type === 'poll' ? `📊 ${s.poll_question}` : s.text_1 || s.media_type || '-'}</td>
                    <td className="px-4 py-3"><Badge variant={s.status === 'pending' ? 'secondary' : 'default'} className="text-xs">{s.status}</Badge></td>
                    <td className="px-4 py-3 text-right">{s.status === 'pending' && <Button variant="ghost" size="sm" onClick={() => deleteScheduled(s.id)} className="text-destructive"><Trash size={14} /></Button>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Reports */}
      {reports.length > 0 && (
        <div className="space-y-3">
          <h3 className="text-lg font-medium" style={{ fontFamily: 'Cabinet Grotesk' }}>📊 Reports</h3>
          <div className="border border-border rounded-md overflow-x-auto">
            <table className="w-full text-sm min-w-[500px]">
              <thead><tr className="bg-muted/50 border-b border-border"><th className="text-left px-4 py-3 text-xs uppercase">Date</th><th className="text-left px-4 py-3 text-xs uppercase">Sent By</th><th className="text-right px-4 py-3 text-xs uppercase">Success</th><th className="text-right px-4 py-3 text-xs uppercase">Failed</th><th className="text-left px-4 py-3 text-xs uppercase">Status</th></tr></thead>
              <tbody>
                {reports.map((r, i) => (
                  <tr key={r.id} className={`border-b border-border ${i % 2 ? 'bg-muted/20' : ''}`}>
                    <td className="px-4 py-3 font-mono text-xs">{r.created_at?.replace('T', ' ').slice(0, 16)}</td>
                    <td className="px-4 py-3">{r.sent_by_name}</td>
                    <td className="px-4 py-3 text-right font-mono text-green-600 dark:text-green-400">{r.success_count}</td>
                    <td className="px-4 py-3 text-right font-mono text-red-600 dark:text-red-400">{r.fail_count}</td>
                    <td className="px-4 py-3"><Badge variant={r.status === 'completed' ? 'default' : 'secondary'} className="text-xs">{r.status}</Badge></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Add Channel Dialog */}
      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Add Telegram Channel</DialogTitle></DialogHeader>
          <form onSubmit={addChannel} className="space-y-4">
            <p className="text-sm text-muted-foreground">Bot must be an admin of the channel.</p>
            <div className="space-y-1.5">
              <Label>Channel Username</Label>
              <Input data-testid="channel-username-input" value={channelUsername} onChange={e => setChannelUsername(e.target.value)} placeholder="@mychannel" required />
            </div>
            <DialogFooter>
              <Button data-testid="add-channel-submit" type="submit" disabled={adding}>{adding ? 'Verifying...' : 'Add Channel'}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
